/**
 * The preview toolbar (W2-02, W3-06; DESIGN.md §4.8): Screen shape (and "same aspect for every player"), Clean | Edit,
 * and every toggle as a button: HUD, line labels, safe areas, hidden-note ghosts, invisible lines, gizmo, auto-key
 * (red when on), sample mode, lane link, readout, gizmo snapping, Phira player settings, view zoom, Theater and Test
 * play (from the playhead, from the start, watch (autoplay) or play (judged)). Every button runs its `preview.*`
 * command (shortcut in the tooltip). The toolbar is always one row (toolbarLayout.ts, UX-051):
 *  - wide: every toggle in the row;
 *  - narrow / compact: icon-only screen button and effects badge, the toggles in one "More preview options"
 *    popover (still one click each, several without closing it); Phira settings, Theater and Test stay in the row.
 * The Test button says what it starts ("Test: watch" or "Test: play", UX-056); the first click asks which one, as
 * long as no mode was chosen yet. The Phira settings popover is mounted in every layout, so
 * `preview.phiraSettings` (palette) opens it at any width. Guided hides the Full-level tools (auto-key, sample mode,
 * lane link, snapping); their commands stay in the palette.
 */

import type { AppServices } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import type { BuiltinShader } from '../../core/model/types';
import { APPROXIMATE_BADGE, BUILTIN_EFFECTS } from '../../render/preview/effects/builtin/index';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { Tooltip } from '../../ui/Tooltip';
import { IconButton } from '../../ui/IconButton';
import { Menu } from '../../ui/Menu';
import { Popover } from '../../ui/Popover';
import { Segmented } from '../../ui/Segmented';
import { Toolbar, ToolbarSeparator } from '../../ui/Toolbar';
import type { MenuItem } from '../../ui/types';
import { runCommand, useEditorValue, useExt, useProjectOpen, useStageState, useStoreValue } from './hooks';
import { PhiraSettingsPopover } from './PhiraSettingsPopover';
import { m } from './preview.messages';
import {
  DEFAULT_SNAP,
  EXT_LANE_LINK,
  EXT_READOUT,
  EXT_SCREEN,
  EXT_SNAP,
  EXT_TEST_MODE,
  laneLinkOf,
  readoutOf,
  SAFE_AREAS,
  SCREEN_SHAPES,
  aspectName,
  screenShapeOf,
  snapPrefsJson,
  snapPrefsOf,
  testPlayModeOf,
  type SnapPrefs,
  type TestPlayMode,
} from './prefs';
import { isIdentityView } from './stageGeometry';
import type { ToolbarLayout } from './toolbarLayout';
import s from './Preview.module.css';

const ref = (key: string, params?: Readonly<Record<string, string | number>>): MessageRef =>
  params === undefined ? { key } : { key, params };

const chartAspectOf = (sv: AppServices): number => sv.store?.doc.pkg.aspectRatio ?? 16 / 9;

const sameSnap = (a: SnapPrefs, b: SnapPrefs): boolean =>
  a.grid === b.grid &&
  a.gridSize === b.gridSize &&
  a.centre === b.centre &&
  a.edges === b.edges &&
  a.anchors === b.anchors;

export interface PreviewToolbarProps {
  readonly services: AppServices;
  /** Defaults to 'wide'. */
  readonly layout?: ToolbarLayout;
}

interface ToggleSpec {
  readonly command: string;
  readonly icon: string;
  readonly label: string;
  readonly pressed: boolean;
  readonly editOnly: boolean;
  readonly full: boolean;
}

/**
 * "Approximate" while a built-in effect is drawn (reimplemented, not Phira's shader; DESIGN.md D24), "Not
 * previewed" for a built-in without a shader; the tooltip names the effects (CR-W2-01b-4).
 */
function builtinEffectsBadge(
  fx: { readonly approximate: readonly BuiltinShader[]; readonly notPreviewed: readonly BuiltinShader[] },
  t: ReturnType<typeof useT>,
  iconOnly: boolean,
) {
  if (fx.approximate.length === 0 && fx.notPreviewed.length === 0) return null;
  const names = (list: readonly BuiltinShader[]): string =>
    list.map((n) => t(BUILTIN_EFFECTS[n].label.key, BUILTIN_EFFECTS[n].label.params)).join(', ');
  const approximate = fx.approximate.length > 0;
  const label = approximate ? t(APPROXIMATE_BADGE.label.key) : t(m.effectsNotPreviewed);
  const detail = [
    ...(approximate
      ? [t(APPROXIMATE_BADGE.hint.key), t(m.effectsApproximateList, { names: names(fx.approximate) })]
      : []),
    ...(fx.notPreviewed.length > 0 ? [t(m.effectsNotPreviewedList, { names: names(fx.notPreviewed) })] : []),
  ].join(' ');
  return (
    <Tooltip content={detail} side="bottom">
      <span
        className={s.effectsBadge}
        tabIndex={0}
        role="note"
        aria-label={`${label}. ${detail}`}
        data-testid="preview-effects-badge"
        data-icon-only={iconOnly ? 'true' : undefined}
      >
        <Icon name="Sparkles" size={12} />
        {!iconOnly && label}
      </span>
    </Tooltip>
  );
}

export function PreviewToolbar(props: PreviewToolbarProps) {
  const { services, layout = 'wide' } = props;
  const compact = layout === 'compact';
  const oneRow = layout !== 'wide';
  const t = useT();
  const open = useProjectOpen(services);
  const preview = useEditorValue(services, (st) => st.preview);
  const level = useEditorValue(services, (st) => st.level);
  const pkgAspect = useStoreValue(services, chartAspectOf);
  const screen = useExt(services, EXT_SCREEN, screenShapeOf);
  const snap = useExt(services, EXT_SNAP, snapPrefsOf, sameSnap);
  const testMode = useExt(services, EXT_TEST_MODE, testPlayModeOf);
  const readout = useExt(services, EXT_READOUT, readoutOf);
  const laneLink = useExt(services, EXT_LANE_LINK, laneLinkOf);
  const stage = useStageState(services);
  const edit = preview.mode === 'edit';
  const full = level === 'full';
  const editReason = edit ? null : ref(m.reasonEditMode);
  const effectsBadge = builtinEffectsBadge(stage.builtinEffects, t, oneRow);
  const noProject = open ? null : ref(m.reasonNoProject);

  const toggles: readonly ToggleSpec[] = [
    {
      command: 'preview.toggleHud',
      icon: 'LayoutDashboard',
      label: m.cmdHud,
      pressed: preview.showHud,
      editOnly: false,
      full: false,
    },
    {
      command: 'preview.toggleLabels',
      icon: 'Tag',
      label: m.cmdLabels,
      pressed: preview.lineLabels,
      editOnly: true,
      full: false,
    },
    {
      command: 'preview.toggleGhosts',
      icon: 'Ghost',
      label: m.cmdGhosts,
      pressed: preview.hiddenGhosts,
      editOnly: true,
      full: false,
    },
    {
      command: 'preview.toggleInvisibleLines',
      icon: 'ScanEye',
      label: m.cmdInvisibleLines,
      pressed: preview.showInvisibleLines,
      editOnly: true,
      full: false,
    },
    {
      command: 'preview.toggleGizmo',
      icon: 'Move',
      label: m.cmdGizmo,
      pressed: preview.gizmo,
      editOnly: true,
      full: false,
    },
    {
      command: 'preview.toggleAutoKey',
      icon: 'CircleDot',
      label: m.cmdAutoKey,
      pressed: preview.autoKey,
      editOnly: true,
      full: true,
    },
    {
      command: 'preview.toggleSampleMode',
      icon: 'Crosshair',
      label: m.cmdSampleMode,
      pressed: preview.sampleMode,
      editOnly: true,
      full: true,
    },
    {
      command: 'preview.toggleLaneLink',
      icon: 'Link2',
      label: m.cmdLaneLink,
      pressed: laneLink,
      editOnly: true,
      full: true,
    },
    {
      command: 'preview.toggleReadout',
      icon: 'PanelBottom',
      label: m.cmdReadout,
      pressed: readout,
      editOnly: false,
      full: false,
    },
  ];
  const visibleToggles = toggles.filter((x) => full || !x.full);

  const screenLabel =
    screen === 'chart'
      ? t(m.screenChart, { aspect: aspectName(pkgAspect) })
      : screen === 'fill'
        ? t(m.screenFill)
        : t(m.screenAs, { shape: screen });

  const screenItems: MenuItem[] = [
    ...SCREEN_SHAPES.map((shape): MenuItem => ({
      kind: 'action',
      icon: shape === screen ? 'CircleCheck' : 'Circle',
      label:
        shape === 'chart'
          ? t(m.screenChart, { aspect: aspectName(pkgAspect) })
          : shape === 'fill'
            ? t(m.screenFill)
            : t(m.screenAs, { shape }),
      onSelect: () => {
        runCommand(services, 'preview.screen', { shape });
      },
    })),
    { kind: 'separator' },
    { kind: 'command', command: 'preview.toggleForceAspect' },
  ];

  const safeItems: MenuItem[] = SAFE_AREAS.map((area): MenuItem => ({
    kind: 'action',
    icon: area === preview.safeAreas ? 'CircleCheck' : 'Circle',
    label: area === 'none' ? t(m.safeAreaNone) : t(m.safeAreaOption, { area }),
    disabledReason: editReason,
    onSelect: () => {
      runCommand(services, 'preview.safeArea', { area });
    },
  }));

  const setSnap = (patch: Partial<SnapPrefs>): void => {
    services.settings.setExt(EXT_SNAP, snapPrefsJson({ ...snap, ...patch }));
  };
  // Gizmo snapping is an Edit-mode tool: disabled with the reason in Clean (the compact More menu shows it too).
  const snapItems: MenuItem[] = [
    {
      kind: 'action',
      icon: snap.grid ? 'SquareCheck' : 'Square',
      label: t(m.snapGrid, { size: snap.gridSize }),
      disabledReason: editReason,
      onSelect: () => {
        setSnap({ grid: !snap.grid });
      },
    },
    {
      kind: 'submenu',
      label: t(m.snapGridSize),
      items: [10, 25, 50, 75, 135].map((size): MenuItem => ({
        kind: 'action',
        icon: size === snap.gridSize ? 'CircleCheck' : 'Circle',
        label: t(m.snapGridSizeOption, { size }),
        disabledReason: editReason,
        onSelect: () => {
          setSnap({ gridSize: size, grid: true });
        },
      })),
    },
    {
      kind: 'action',
      icon: snap.centre ? 'SquareCheck' : 'Square',
      label: t(m.snapCentre),
      disabledReason: editReason,
      onSelect: () => {
        setSnap({ centre: !snap.centre });
      },
    },
    {
      kind: 'action',
      icon: snap.edges ? 'SquareCheck' : 'Square',
      label: t(m.snapEdges),
      disabledReason: editReason,
      onSelect: () => {
        setSnap({ edges: !snap.edges });
      },
    },
    {
      kind: 'action',
      icon: snap.anchors ? 'SquareCheck' : 'Square',
      label: t(m.snapAnchors),
      disabledReason: editReason,
      onSelect: () => {
        setSnap({ anchors: !snap.anchors });
      },
    },
    { kind: 'separator' },
    {
      kind: 'action',
      icon: 'RotateCcw',
      label: t(m.snapReset),
      disabledReason: editReason,
      onSelect: () => {
        services.settings.setExt(EXT_SNAP, snapPrefsJson(DEFAULT_SNAP));
      },
    },
  ];

  const testItems: MenuItem[] = [
    { kind: 'command', command: 'preview.testPlay', args: { from: 'playhead' }, label: t(m.fromPlayhead) },
    { kind: 'command', command: 'preview.testPlay', args: { from: 'start' }, label: t(m.fromStart) },
    { kind: 'separator' },
    {
      kind: 'action',
      icon: testMode === 'autoplay' ? 'CircleCheck' : 'Circle',
      label: t(m.modeAutoplay),
      onSelect: () => {
        if (testMode !== 'autoplay') runCommand(services, 'preview.toggleJudged');
      },
    },
    {
      kind: 'action',
      icon: testMode === 'judged' ? 'CircleCheck' : 'Circle',
      label: t(m.modeJudged),
      onSelect: () => {
        if (testMode !== 'judged') runCommand(services, 'preview.toggleJudged');
      },
    },
  ];

  const zoomed = edit && !isIdentityView(stage.view);
  const phira = <PhiraSettingsPopover services={services} open={stage.phiraOpen} graceAt={stage.phiraGraceAt} />;

  /** Every toggle as a button; the narrow layouts show them in the More popover, without the Phira button. */
  const toggleGroup = (where: ToolbarLayout, withPhira: boolean) => (
    <div className={where === 'wide' ? s.toolbarGroup : s.moreGrid} data-toolbar-toggles={where}>
      {visibleToggles.map((x) => (
        <IconButton
          key={x.command}
          icon={x.icon}
          size="sm"
          label={ref(x.label)}
          command={x.command}
          pressed={x.pressed}
          className={x.command === 'preview.toggleAutoKey' && x.pressed ? s.autoKeyOn : undefined}
          disabledReason={x.editOnly ? editReason : null}
          data-toggle={x.command}
          onClick={() => {
            runCommand(services, x.command);
          }}
        />
      ))}
      <Menu
        label={t(m.cmdSafeArea)}
        trigger={
          <IconButton
            icon="Scan"
            size="sm"
            label={ref(m.cmdSafeArea)}
            pressed={preview.safeAreas !== 'none'}
            disabledReason={editReason}
            data-toggle="preview.safeArea"
          />
        }
        items={safeItems}
      />
      {full && (
        <Menu
          label={t(m.snapMenu)}
          trigger={
            <IconButton
              icon="Magnet"
              size="sm"
              label={ref(m.snapMenu)}
              disabledReason={editReason}
              data-toggle="preview.snap"
            />
          }
          items={snapItems}
        />
      )}
      {withPhira && phira}
    </div>
  );

  /** Overlays switched on in the More popover (its trigger shows that some are on). */
  const anyOn = visibleToggles.some(
    (x) => x.pressed && x.command !== 'preview.toggleHud' && x.command !== 'preview.toggleReadout',
  );

  const zoomGroup = zoomed && (
    <div className={s.toolbarGroup}>
      <span className={s.zoomLabel} aria-live="polite">
        {t(m.zoomPercent, { percent: Math.round(stage.view.zoom * 100) })}
      </span>
      <IconButton
        icon="Expand"
        size="sm"
        label={ref(m.cmdResetView)}
        command="preview.resetView"
        onClick={() => {
          runCommand(services, 'preview.resetView');
        }}
      />
    </div>
  );

  const screenTrigger = oneRow ? (
    <IconButton icon="MonitorSmartphone" size="sm" label={ref(m.screenButton, { screen: screenLabel })} />
  ) : (
    <Button size="sm" variant="ghost" icon="MonitorSmartphone" aria-label={t(m.screenButton, { screen: screenLabel })}>
      <span className={s.screenButtonLabel}>{screenLabel}</span>
    </Button>
  );

  /** Test: asks "watch or play?" the first time (no mode chosen yet), then starts from the playhead. */
  const startTest = async (): Promise<void> => {
    if (services.settings.getExt(EXT_TEST_MODE) === undefined) {
      const chosen = await services.ui.choose<TestPlayMode>({
        title: ref(m.testChooseTitle),
        body: ref(m.testChooseBody),
        options: [
          { value: 'autoplay', label: ref(m.testChooseWatch), description: ref(m.testChooseWatchDesc) },
          { value: 'judged', label: ref(m.testChoosePlay), description: ref(m.testChoosePlayDesc) },
        ],
      });
      if (chosen === null) return;
      services.settings.setExt(EXT_TEST_MODE, chosen);
    }
    runCommand(services, 'preview.testPlay', { from: 'playhead' });
  };
  const testIcon = testMode === 'judged' ? 'Gamepad2' : 'MonitorPlay';
  const testName = t(testMode === 'judged' ? m.testJudged : m.testAutoplay);
  const testButton = compact ? (
    <IconButton
      icon={testIcon}
      size="sm"
      label={ref(testMode === 'judged' ? m.testJudged : m.testAutoplay)}
      command="preview.testPlay"
      disabledReason={noProject}
      data-action="preview.testPlay"
      onClick={() => {
        void startTest();
      }}
    />
  ) : (
    <Button
      size="sm"
      variant="primary"
      icon={testIcon}
      command="preview.testPlay"
      disabledReason={noProject}
      aria-label={testName}
      data-action="preview.testPlay"
      data-test-mode={testMode}
      onClick={() => {
        void startTest();
      }}
    >
      {oneRow
        ? t(testMode === 'judged' ? m.testPlayShort : m.testWatchShort)
        : t(testMode === 'judged' ? m.testPlayWide : m.testWatchWide)}
    </Button>
  );

  return (
    <Toolbar label={ref(m.toolbarLabel)} className={s.toolbar}>
      <Menu label={t(m.screenMenu)} trigger={screenTrigger} items={screenItems} />
      <Segmented<'clean' | 'edit'>
        ariaLabel={t(m.modeLabel)}
        size="sm"
        value={preview.mode}
        options={[
          { value: 'clean', label: t(m.modeClean) },
          { value: 'edit', label: t(m.modeEdit) },
        ]}
        onChange={(v) => {
          if (v !== preview.mode) runCommand(services, 'preview.toggleMode');
        }}
      />
      {layout === 'wide' && (
        <>
          <ToolbarSeparator />
          {toggleGroup('wide', true)}
        </>
      )}
      {effectsBadge}
      <span className={s.toolbarSpacer} />
      {zoomGroup}
      {oneRow && (
        <div className={s.toolbarGroup}>
          <Popover
            label={t(m.moreMenu)}
            side="bottom"
            align="end"
            contentClassName={s.morePopover}
            trigger={
              <IconButton
                icon="SlidersHorizontal"
                size="sm"
                label={ref(m.moreMenu)}
                className={anyOn ? s.moreOn : undefined}
                data-toggle="preview.more"
              />
            }
          >
            <p className={s.moreTitle}>{t(m.moreTitle)}</p>
            {toggleGroup(layout, false)}
          </Popover>
          {phira}
        </div>
      )}
      <div className={s.toolbarGroup}>
        <IconButton
          icon="Maximize"
          size="sm"
          label={ref(m.cmdTheater)}
          command="preview.theater"
          pressed={preview.theater}
          disabledReason={noProject}
          data-toggle="preview.theater"
          onClick={() => {
            runCommand(services, 'preview.theater');
          }}
        />
        {testButton}
        <Menu
          label={t(m.testMenu)}
          trigger={<IconButton icon="ChevronDown" size="sm" label={ref(m.testMenu)} disabledReason={noProject} />}
          items={testItems}
        />
      </div>
    </Toolbar>
  );
}
